/* eslint-disable no-loop-func */
import { useEffect, useRef } from 'react';

// Effets visuels dessinés (canvas) selon l'ambiance : vrais feux d'artifice,
// feuilles, flocons, guirlande de Noël, chauves-souris, pétales, cœurs…
// Décoratif : aucun clic capturé, mis en pause onglet masqué, coupé si
// « réduire les animations » est activé.

const rnd = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const TAU = Math.PI * 2;

// ── Formes (dessinées autour de 0,0, taille s) ───────────────────────────────
const SHAPES = {
  dot(c, s, col) { c.fillStyle = col; c.beginPath(); c.arc(0, 0, s * .35, 0, TAU); c.fill(); },
  leaf(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.moveTo(0, -s);
    c.quadraticCurveTo(s * .95, -s * .15, 0, s); c.quadraticCurveTo(-s * .95, -s * .15, 0, -s); c.fill();
    c.strokeStyle = 'rgba(0,0,0,.25)'; c.lineWidth = Math.max(.6, s * .06);
    c.beginPath(); c.moveTo(0, -s * .9); c.lineTo(0, s * 1.15); c.stroke();
  },
  flake(c, s, col) {
    c.strokeStyle = col; c.lineWidth = Math.max(.8, s * .09); c.lineCap = 'round';
    for (let i = 0; i < 6; i++) {
      c.save(); c.rotate((i * TAU) / 6);
      c.beginPath(); c.moveTo(0, 0); c.lineTo(0, -s);
      c.moveTo(0, -s * .55); c.lineTo(s * .28, -s * .8); c.moveTo(0, -s * .55); c.lineTo(-s * .28, -s * .8);
      c.stroke(); c.restore();
    }
  },
  petal(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.moveTo(0, -s);
    c.bezierCurveTo(s * .9, -s * .7, s * .8, s * .6, 0, s);
    c.bezierCurveTo(-s * .8, s * .6, -s * .9, -s * .7, 0, -s); c.fill();
  },
  heart(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.moveTo(0, s * .8);
    c.bezierCurveTo(-s * 1.3, -s * .1, -s * .6, -s * 1.1, 0, -s * .35);
    c.bezierCurveTo(s * .6, -s * 1.1, s * 1.3, -s * .1, 0, s * .8); c.fill();
  },
  egg(c, s, col) {
    c.save(); c.beginPath(); c.ellipse(0, 0, s * .7, s, 0, 0, TAU); c.fillStyle = col; c.fill(); c.clip();
    c.fillStyle = 'rgba(255,255,255,.55)'; c.fillRect(-s, -s * .25, s * 2, s * .22);
    c.fillStyle = 'rgba(0,0,0,.18)'; c.fillRect(-s, s * .25, s * 2, s * .18); c.restore();
  },
  pencil(c, s, col) {
    c.fillStyle = col; c.fillRect(-s * .18, -s, s * .36, s * 1.5);
    c.fillStyle = '#F2D3A0'; c.beginPath(); c.moveTo(-s * .18, s * .5); c.lineTo(s * .18, s * .5); c.lineTo(0, s * 1.05); c.fill();
    c.fillStyle = '#EF8FA0'; c.fillRect(-s * .18, -s * 1.15, s * .36, s * .18);
  },
  crepe(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.arc(0, 0, s * .8, 0, TAU); c.fill();
    c.fillStyle = 'rgba(255,255,255,.35)'; c.beginPath(); c.arc(-s * .15, -s * .15, s * .45, 0, TAU); c.fill();
    c.fillStyle = 'rgba(120,70,10,.35)'; for (let i = 0; i < 5; i++) { c.beginPath(); c.arc(Math.cos(i * 1.7) * s * .5, Math.sin(i * 2.3) * s * .5, s * .06, 0, TAU); c.fill(); }
  },
  confetti(c, s, col) { c.fillStyle = col; c.fillRect(-s * .4, -s * .2, s * .8, s * .4); },
  sparkle(c, s, col) {
    c.fillStyle = col; c.beginPath();
    for (let i = 0; i < 8; i++) { const r = i % 2 ? s * .22 : s; const a = (i * TAU) / 8; c.lineTo(Math.cos(a) * r, Math.sin(a) * r); }
    c.closePath(); c.fill();
  },
  flower(c, s, col, t, center = '#F5C842') {
    c.fillStyle = col;
    for (let i = 0; i < 5; i++) { c.save(); c.rotate((i * TAU) / 5); c.beginPath(); c.ellipse(0, -s * .55, s * .3, s * .5, 0, 0, TAU); c.fill(); c.restore(); }
    c.fillStyle = center; c.beginPath(); c.arc(0, 0, s * .26, 0, TAU); c.fill();
  },
  poppy(c, s, col) { SHAPES.flower(c, s, col, 0, '#2A1A1A'); },
  clover(c, s, col) {
    for (let i = 0; i < 4; i++) { c.save(); c.rotate((i * TAU) / 4); c.translate(0, -s * .42); SHAPES.heart(c, s * .55, col); c.restore(); }
    c.strokeStyle = col; c.lineWidth = Math.max(1, s * .1); c.lineCap = 'round'; c.beginPath(); c.moveTo(0, 0); c.quadraticCurveTo(s * .15, s * .6, s * .45, s * .9); c.stroke();
  },
  fish(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.ellipse(0, 0, s * .9, s * .5, 0, 0, TAU); c.fill();
    c.beginPath(); c.moveTo(s * .75, 0); c.lineTo(s * 1.35, -s * .5); c.lineTo(s * 1.35, s * .5); c.closePath(); c.fill();
    c.fillStyle = '#FFFFFF'; c.beginPath(); c.arc(-s * .45, -s * .1, s * .13, 0, TAU); c.fill();
    c.fillStyle = '#1C1612'; c.beginPath(); c.arc(-s * .48, -s * .1, s * .06, 0, TAU); c.fill();
  },
  muguet(c, s) {
    c.strokeStyle = '#4C9A5F'; c.lineWidth = Math.max(1, s * .08); c.lineCap = 'round';
    c.beginPath(); c.moveTo(0, s); c.quadraticCurveTo(-s * .2, 0, s * .15, -s * .85); c.stroke();
    c.fillStyle = '#4C9A5F'; c.beginPath(); c.ellipse(-s * .22, s * .55, s * .12, s * .5, -.5, 0, TAU); c.fill();
    for (const [x, y, r] of [[s * .1, -s * .75, .2], [-s * .02, -s * .45, .22], [-s * .12, -s * .12, .22]]) {
      c.fillStyle = '#FFFFFF'; c.strokeStyle = 'rgba(0,0,0,.18)'; c.lineWidth = .8; c.beginPath(); c.arc(x, y, s * r, 0, TAU); c.fill(); c.stroke();
    }
  },
  tie(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.moveTo(-s * .22, -s * .9); c.lineTo(s * .22, -s * .9); c.lineTo(s * .13, -s * .55); c.lineTo(-s * .13, -s * .55); c.closePath(); c.fill();
    c.beginPath(); c.moveTo(-s * .13, -s * .55); c.lineTo(s * .13, -s * .55); c.lineTo(s * .42, s * .6); c.lineTo(0, s * .95); c.lineTo(-s * .42, s * .6); c.closePath(); c.fill();
    c.fillStyle = 'rgba(255,255,255,.3)'; c.fillRect(-s * .3, 0, s * .6, s * .12);
  },
  note(c, s, col) {
    c.fillStyle = col; c.strokeStyle = col; c.lineWidth = Math.max(1.2, s * .12); c.lineCap = 'round';
    c.beginPath(); c.ellipse(-s * .25, s * .6, s * .32, s * .22, -.4, 0, TAU); c.fill();
    c.beginPath(); c.moveTo(s * .02, s * .52); c.lineTo(s * .02, -s * .8); c.quadraticCurveTo(s * .5, -s * .6, s * .5, -s * .1); c.stroke();
  },
  crown(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.moveTo(-s * .8, s * .55); c.lineTo(-s * .8, -s * .35); c.lineTo(-s * .4, 0); c.lineTo(0, -s * .6); c.lineTo(s * .4, 0); c.lineTo(s * .8, -s * .35); c.lineTo(s * .8, s * .55); c.closePath(); c.fill();
    c.fillStyle = 'rgba(255,255,255,.5)'; c.fillRect(-s * .8, s * .3, s * 1.6, s * .12);
    c.fillStyle = '#E74C3C'; c.beginPath(); c.arc(0, s * .05, s * .1, 0, TAU); c.fill();
  },
  balloon(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.ellipse(0, -s * .2, s * .55, s * .7, 0, 0, TAU); c.fill();
    c.beginPath(); c.moveTo(0, s * .5); c.lineTo(-s * .1, s * .65); c.lineTo(s * .1, s * .65); c.closePath(); c.fill();
    c.fillStyle = 'rgba(255,255,255,.4)'; c.beginPath(); c.ellipse(-s * .2, -s * .45, s * .12, s * .2, .4, 0, TAU); c.fill();
    c.strokeStyle = 'rgba(0,0,0,.3)'; c.lineWidth = 1; c.beginPath(); c.moveTo(0, s * .65); c.quadraticCurveTo(s * .2, s * .95, 0, s * 1.25); c.stroke();
  },
  ghost(c, s, col) {
    c.fillStyle = col; c.beginPath(); c.arc(0, -s * .15, s * .6, Math.PI, 0);
    c.lineTo(s * .6, s * .8); for (let i = 0; i < 3; i++) c.quadraticCurveTo(s * (.4 - i * .4), s * .5, s * (.2 - i * .4), s * .8);
    c.lineTo(-s * .6, s * .8); c.closePath(); c.fill();
    c.fillStyle = '#1C1612'; c.beginPath(); c.arc(-s * .2, -s * .2, s * .07, 0, TAU); c.arc(s * .2, -s * .2, s * .07, 0, TAU); c.fill();
  },
  bat(c, s, col, t = 0) {
    const f = Math.sin(t * 14) * .6; // battement
    c.fillStyle = col;
    for (const d of [-1, 1]) {
      c.save(); c.scale(d, 1); c.beginPath(); c.moveTo(0, 0);
      c.quadraticCurveTo(s * .5, -s * (.9 + f * .5), s * 1.2, -s * (.3 + f));
      c.quadraticCurveTo(s * .95, -s * .05, s * .75, s * .1);
      c.quadraticCurveTo(s * .5, -s * .05, s * .3, s * .15);
      c.quadraticCurveTo(s * .12, s * .05, 0, s * .35); c.fill(); c.restore();
    }
    c.beginPath(); c.ellipse(0, s * .05, s * .17, s * .3, 0, 0, TAU); c.fill();
    c.beginPath(); c.moveTo(-s * .12, -s * .2); c.lineTo(-s * .08, -s * .45); c.lineTo(-s * .02, -s * .22); c.moveTo(s * .12, -s * .2); c.lineTo(s * .08, -s * .45); c.lineTo(s * .02, -s * .22); c.fill();
  },
};

// ── Ambiances ────────────────────────────────────────────────────────────────
const FLAKE_L = ['#7FB2E0', '#9CC3E8', '#B7D3EE'], FLAKE_D = ['#FFFFFF', '#E3F0FF', '#CFE4FA'];
const CFG = {
  epiphanie: { layers: [{ t: 'fall', shapes: ['crown', 'sparkle'], colors: ['#F5C842', '#E8B93A', '#FFD54A'], n: 16, size: [10, 18], vy: [26, 55], sway: 30, alpha: .92 }] },
  carnaval:  { layers: [{ t: 'rise', shapes: ['balloon'], colors: ['#E91E63', '#F5C842', '#3B6FBF', '#16A34A', '#9B59B6'], n: 12, size: [14, 22], vy: [30, 60], sway: 30, alpha: .9 }, { t: 'fall', shapes: ['confetti'], colors: ['#E91E63', '#F5C842', '#3B6FBF', '#16A34A'], n: 30, size: [7, 12], vy: [45, 90], sway: 40, alpha: .9 }] },
  stpatrick: { layers: [{ t: 'fall', shapes: ['clover', 'clover', 'sparkle'], colors: ['#1F8A3B', '#2EA84E', '#4CC773', '#D4AF37'], n: 20, size: [9, 17], vy: [26, 55], sway: 32, alpha: .92 }] },
  poissonavril:{ layers: [{ t: 'fall', shapes: ['fish'], colors: ['#3B8FD0', '#F39C12', '#E74C3C', '#5CC8FF', '#9B59B6'], n: 16, size: [9, 15], vy: [26, 55], sway: 50, alpha: .92 }] },
  muguet:    { layers: [{ t: 'fall', shapes: ['muguet'], colors: ['#FFFFFF'], n: 18, size: [12, 20], vy: [22, 46], sway: 40, alpha: .95 }] },
  fetemeres: { layers: [{ t: 'fall', shapes: ['flower', 'flower', 'heart'], colors: ['#F27AAA', '#FFB3C6', '#F5C842', '#E56B9A', '#FFFFFF'], n: 20, size: [8, 16], vy: [24, 52], sway: 36, alpha: .92 }] },
  feteperes: { layers: [{ t: 'fall', shapes: ['tie', 'tie', 'sparkle'], colors: ['#2A55A8', '#3B6FBF', '#C0392B', '#5A6B7B', '#F5C842'], n: 16, size: [10, 17], vy: [28, 58], sway: 30, alpha: .92 }] },
  fetemusique:{ layers: [{ t: 'rise', shapes: ['note'], colors: ['#7B3FD0', '#F04E8A', '#3B6FBF', '#F5A623', '#16A34A'], n: 22, size: [10, 18], vy: [28, 62], sway: 40, alpha: .9 }] },
  toussaint: { layers: [{ t: 'fall', shapes: ['flower', 'leaf'], colors: ['#F5C842', '#E67E22', '#FFFFFF', '#C0392B', '#B5651D'], n: 16, size: [8, 15], vy: [24, 50], sway: 34, alpha: .9 }] },
  armistice: { layers: [{ t: 'fall', shapes: ['poppy', 'poppy', 'flower'], colors: ['#C0392B', '#D93636', '#C0392B', '#3B6FBF'], n: 16, size: [8, 14], vy: [24, 50], sway: 34, alpha: .92 }] },
  avent:     { layers: [{ t: 'fall', shapes: ['sparkle', 'dot', 'flake'], colors: 'flake', n: 30, size: [6, 13], vy: [24, 55], sway: 22, alpha: .9 }, { t: 'fall', shapes: ['sparkle'], colors: ['#F5C842', '#FFD54A'], n: 8, size: [8, 14], vy: [20, 40], sway: 20, alpha: .95 }] },
  automne:   { layers: [{ t: 'fall', shapes: ['leaf'], colors: ['#D2691E', '#C0392B', '#E67E22', '#B8860B', '#A0522D', '#D4AF37'], n: 22, size: [9, 19], vy: [28, 70], sway: 40, alpha: .9 }] },
  hiver:     { layers: [{ t: 'fall', shapes: ['flake', 'flake', 'dot'], colors: 'flake', n: 38, size: [6, 14], vy: [22, 55], sway: 26, alpha: .9 }] },
  noel:      { layers: [{ t: 'fall', shapes: ['dot', 'dot', 'flake'], colors: 'flake', n: 40, size: [5, 13], vy: [24, 58], sway: 22, alpha: .9 }, { t: 'lights' }] },
  printemps: { layers: [{ t: 'fall', shapes: ['petal'], colors: ['#F8B4CB', '#F28DB0', '#FFD1DC', '#FFC2D6'], n: 24, size: [7, 14], vy: [20, 45], sway: 55, alpha: .9 }] },
  ete:       { layers: [{ t: 'sun' }, { t: 'bokeh', colors: ['#FFD166', '#FFB703', '#FF9E00', '#FFE08A'], n: 16 }] },
  valentin:  { layers: [{ t: 'rise', shapes: ['heart'], colors: ['#E91E63', '#FF6F91', '#FF8FA3', '#C2185B'], n: 20, size: [8, 17], vy: [26, 60], sway: 35, alpha: .85 }] },
  chandeleur:{ layers: [{ t: 'fall', shapes: ['crepe', 'crepe', 'sparkle'], colors: ['#E3A93B', '#EDB95A', '#D9962A', '#F5C842'], n: 18, size: [9, 18], vy: [26, 55], sway: 30, alpha: .9 }] },
  rentree:   { layers: [{ t: 'fall', shapes: ['pencil', 'pencil', 'sparkle'], colors: ['#F5C842', '#3B6FBF', '#E74C3C', '#5A9E7A', '#9B59B6'], n: 18, size: [10, 17], vy: [30, 65], sway: 28, alpha: .92 }] },
  paques:    { layers: [{ t: 'fall', shapes: ['egg', 'petal'], colors: ['#F5C842', '#B07CD8', '#6FBF9A', '#FFB3C6', '#7FB2E0'], n: 20, size: [8, 15], vy: [24, 50], sway: 38, alpha: .92 }] },
  halloween: { layers: [{ t: 'moon' }, { t: 'rise', shapes: ['ghost'], colors: ['rgba(255,255,255,.85)'], n: 4, size: [16, 24], vy: [14, 26], sway: 30, alpha: .8 }, { t: 'fly', n: 6 }] },
  nouvelan:  { layers: [{ t: 'fireworks', palette: ['#FFD54A', '#FF5C8A', '#5CC8FF', '#FFFFFF', '#9B7BFF', '#7CFFB2'] }, { t: 'fall', shapes: ['confetti', 'sparkle'], colors: ['#FFD54A', '#FF5C8A', '#5CC8FF', '#9B7BFF', '#7CFFB2'], n: 26, size: [7, 12], vy: [40, 85], sway: 40, alpha: .9 }] },
  fetenat:   { layers: [{ t: 'fireworks', palette: ['#3B6FBF', '#FFFFFF', '#E23B3B', '#6FA0F0', '#FF6B6B'] }] },
};

export default function SeasonFX({ season, isDark, level = 'normal' }) {
  const ref = useRef(null);

  useEffect(() => {
    const cfg = season && CFG[season.id];
    const canvas = ref.current;
    if (!cfg || !canvas || level === 'off') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = canvas.getContext('2d');
    const small = window.innerWidth < 600;
    const dpr = Math.min(window.devicePixelRatio || 1, small ? 1.5 : 2);
    let W = 0, H = 0;
    const resize = () => {
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    const mult = level === 'low' ? 0.45 : level === 'high' ? 1.8 : 1;
    const scale = (small ? 0.65 : 1) * mult;
    const layers = cfg.layers.map((L) => {
      const colors = L.colors === 'flake' ? (isDark ? FLAKE_D : FLAKE_L) : L.colors;
      const mk = (initial) => {
        const rise = L.t === 'rise';
        return {
          x: rnd(0, W), y: initial ? rnd(0, H) : (rise ? H + 30 : -30),
          vy: rnd(...(L.vy || [30, 60])) * (rise ? -1 : 1), ph: rnd(0, TAU), fq: rnd(.5, 1.4),
          rot: rnd(0, TAU), vr: rnd(-1.2, 1.2), s: rnd(...(L.size || [10, 16])),
          shape: L.shapes ? pick(L.shapes) : null, col: colors ? pick(colors) : null,
        };
      };
      const n = Math.max(2, Math.round((L.n || 0) * scale));
      const parts = L.n ? Array.from({ length: n }, () => mk(true)) : [];
      let bats = [];
      if (L.t === 'fly') bats = Array.from({ length: Math.max(2, Math.round(L.n * scale)) }, () => ({ x: rnd(-W, W), y: rnd(H * .08, H * .55), v: rnd(60, 120) * (Math.random() < .5 ? -1 : 1), s: rnd(9, 15), ph: rnd(0, TAU) }));
      return { L, parts, mk, bats, rockets: [], sparks: [], next: 0.4 };
    });

    // ── feux d'artifice ──
    const burst = (lay, x, y, col) => {
      const kind = pick(['sphere', 'sphere', 'ring', 'willow']);
      const n = kind === 'ring' ? 46 : 70;
      const sp = rnd(90, 190) * (small ? .8 : 1);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * TAU + rnd(-.05, .05);
        const v = kind === 'ring' ? sp : sp * rnd(.35, 1);
        lay.sparks.push({ x, y, px: x, py: y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0, max: kind === 'willow' ? rnd(1.9, 2.6) : rnd(1.1, 1.7), g: kind === 'willow' ? 55 : 95, col: Math.random() < .2 ? '#FFFFFF' : col, w: kind === 'willow' ? 1.4 : 1.8 });
      }
      lay.flash = { x, y, a: .55, col };
    };

    let last = performance.now(), t = 0, raf = 0, paused = false;
    const onVis = () => { paused = document.hidden; last = performance.now(); };
    document.addEventListener('visibilitychange', onVis);

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (paused) return;
      const dt = Math.min(.05, (now - last) / 1000); last = now; t += dt;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      for (const lay of layers) {
        const { L } = lay;
        if (L.t === 'fall' || L.t === 'rise') {
          ctx.globalAlpha = L.alpha || .9;
          for (const p of lay.parts) {
            p.y += p.vy * dt; p.rot += p.vr * dt; p.ph += p.fq * dt;
            const x = p.x + Math.sin(p.ph) * (L.sway || 30);
            if (p.vy > 0 ? p.y > H + 30 : p.y < -30) Object.assign(p, lay.mk(false), { x: rnd(0, W) });
            ctx.save(); ctx.translate(x, p.y); ctx.rotate(p.rot * (p.shape === 'ghost' ? 0 : 1)); ctx.scale(1, p.shape === 'leaf' || p.shape === 'petal' ? Math.cos(p.ph * 1.3) * .5 + .75 : 1);
            SHAPES[p.shape](ctx, p.s, p.col); ctx.restore();
          }
          ctx.globalAlpha = 1;
        } else if (L.t === 'lights') {
          // Guirlande lumineuse en haut de l'écran
          const y0 = 56, step = small ? 44 : 58, cols = ['#FF3B3B', '#2ECC71', '#FFD54A', '#4DA3FF'];
          ctx.strokeStyle = isDark ? 'rgba(255,255,255,.28)' : 'rgba(60,40,30,.35)'; ctx.lineWidth = 1.4; ctx.beginPath();
          for (let x = 0; x <= W + step; x += step) { const m = x + step / 2; if (x === 0) ctx.moveTo(0, y0 - 6); ctx.quadraticCurveTo(m, y0 + 16, x + step, y0 - 6); }
          ctx.stroke();
          let i = 0;
          for (let x = step / 2; x < W + step; x += step, i++) {
            const by = y0 + 11, on = .55 + .45 * Math.sin(t * 2.2 + i * 1.3), c = cols[i % cols.length];
            ctx.save(); ctx.translate(x, by); ctx.globalAlpha = .25 * on; const g = ctx.createRadialGradient(0, 4, 1, 0, 4, 17); g.addColorStop(0, c); g.addColorStop(1, 'transparent'); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 4, 17, 0, TAU); ctx.fill();
            ctx.globalAlpha = .55 + .45 * on; ctx.fillStyle = c; ctx.beginPath(); ctx.ellipse(0, 5, 3.6, 5.4, 0, 0, TAU); ctx.fill(); ctx.fillStyle = '#5b4636'; ctx.fillRect(-1.7, -1.5, 3.4, 3); ctx.restore();
          }
        } else if (L.t === 'sun') {
          const cx = W * .92, cy = -10;
          const g = ctx.createRadialGradient(cx, cy, 4, cx, cy, W * .75);
          g.addColorStop(0, isDark ? 'rgba(255,190,60,.40)' : 'rgba(255,190,60,.55)'); g.addColorStop(.35, 'rgba(255,190,60,.12)'); g.addColorStop(1, 'rgba(255,190,60,0)');
          ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
          ctx.save(); ctx.translate(cx, cy); ctx.rotate(t * .07); ctx.fillStyle = isDark ? 'rgba(255,200,80,.08)' : 'rgba(255,190,60,.12)';
          for (let i = 0; i < 12; i++) { ctx.rotate(TAU / 12); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(W * 1.2, -34); ctx.lineTo(W * 1.2, 34); ctx.fill(); }
          ctx.restore();
          ctx.fillStyle = '#FFC93C'; ctx.beginPath(); ctx.arc(cx, cy, 52, 0, TAU); ctx.fill();
        } else if (L.t === 'bokeh') {
          if (!lay.parts.length) lay.parts = Array.from({ length: Math.round(L.n * scale) }, () => ({ x: rnd(0, W), y: rnd(0, H), r: rnd(10, 34), v: rnd(8, 22), ph: rnd(0, TAU), c: pick(L.colors) }));
          for (const p of lay.parts) { p.y -= p.v * dt; p.ph += dt; if (p.y < -40) { p.y = H + 40; p.x = rnd(0, W); } ctx.globalAlpha = .13 + .07 * Math.sin(p.ph); ctx.fillStyle = p.c; ctx.beginPath(); ctx.arc(p.x + Math.sin(p.ph) * 14, p.y, p.r, 0, TAU); ctx.fill(); }
          ctx.globalAlpha = 1;
        } else if (L.t === 'moon') {
          const cx = W * .68, cy = 28, r = 16;
          const g = ctx.createRadialGradient(cx, cy, r * .6, cx, cy, r * 3.4); g.addColorStop(0, 'rgba(255,214,140,.38)'); g.addColorStop(1, 'rgba(255,214,140,0)');
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r * 3.4, 0, TAU); ctx.fill();
          ctx.fillStyle = '#FFE9B0'; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
          ctx.fillStyle = 'rgba(200,160,90,.35)'; for (const [dx, dy, rr] of [[-4, -3, 3], [4, 4, 4], [3, -6, 2]]) { ctx.beginPath(); ctx.arc(cx + dx, cy + dy, rr, 0, TAU); ctx.fill(); }
        } else if (L.t === 'fly') {
          for (const b of lay.bats) {
            b.x += b.v * dt; b.ph += dt * 2.2;
            if (b.v > 0 && b.x > W + 60) { b.x = -60; b.y = rnd(H * .08, H * .55); } else if (b.v < 0 && b.x < -60) { b.x = W + 60; b.y = rnd(H * .08, H * .55); }
            ctx.save(); ctx.translate(b.x, b.y + Math.sin(b.ph) * 22); ctx.rotate(Math.sin(b.ph) * .12); ctx.globalAlpha = .92;
            SHAPES.bat(ctx, b.s, isDark ? '#07040B' : '#2A1635', t + b.ph); ctx.restore();
          }
        } else if (L.t === 'fireworks') {
          lay.next -= dt;
          if (lay.next <= 0 && lay.rockets.length < (level === 'high' ? 4 : 2)) {
            lay.next = rnd(.8, 1.7) / mult;
            lay.rockets.push({ x: rnd(W * .12, W * .88), y: H, ty: rnd(H * .16, H * .5), col: pick(L.palette), tr: [] });
          }
          ctx.globalCompositeOperation = isDark ? 'lighter' : 'source-over';
          for (let i = lay.rockets.length - 1; i >= 0; i--) {
            const r = lay.rockets[i]; r.y -= (H * .9) * dt * (.55 + (r.y - r.ty) / H); r.tr.push([r.x, r.y]); if (r.tr.length > 9) r.tr.shift();
            ctx.strokeStyle = r.col; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.beginPath(); r.tr.forEach(([x, y], k) => (k ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.stroke();
            if (r.y <= r.ty) { burst(lay, r.x, r.y, r.col); if (Math.random() < .35) burst(lay, r.x + rnd(-40, 40), r.y + rnd(-20, 20), pick(L.palette)); lay.rockets.splice(i, 1); }
          }
          if (lay.flash) { const f = lay.flash; ctx.globalAlpha = f.a; const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, 150); g.addColorStop(0, f.col); g.addColorStop(1, 'transparent'); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(f.x, f.y, 150, 0, TAU); ctx.fill(); f.a -= dt * 1.6; if (f.a <= 0) lay.flash = null; ctx.globalAlpha = 1; }
          for (let i = lay.sparks.length - 1; i >= 0; i--) {
            const s = lay.sparks[i]; s.life += dt; if (s.life > s.max) { lay.sparks.splice(i, 1); continue; }
            s.px = s.x; s.py = s.y; s.vx *= .985; s.vy = s.vy * .985 + s.g * dt; s.x += s.vx * dt; s.y += s.vy * dt;
            const k = 1 - s.life / s.max; ctx.globalAlpha = Math.min(1, k * 1.4); ctx.strokeStyle = s.col; ctx.lineWidth = s.w; ctx.lineCap = 'round';
            ctx.beginPath(); ctx.moveTo(s.px, s.py); ctx.lineTo(s.x, s.y); ctx.stroke();
          }
          ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
        }
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [season?.id, isDark, level]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!season || !CFG[season.id] || level === 'off') return null;
  return <canvas ref={ref} className="season-fx" aria-hidden="true" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 40 }} />;
}
